import {DropdownMenu} from '@wandb/components';
import React, {useMemo} from 'react';

import {useSweepsContext} from '../../../pages/Sweeps/SweepsContext';
import type {Sort} from '../../../util/queryTypes';
import {useRuns} from '../RunSelectorRunsContext';
import {useRunSetSort} from '../RunSelectorRunSetContext';
import {
  getShouldHideRandomizeColors,
  getShouldHideResetColors,
} from '../WBTable/ResetRunColors';
import type {WBTableColumn} from '../WBTable/types';
import {useResetColors, useResetColorsInfo} from '../WBTable/useResetColors';
import {MenuItem} from './MenuItem';

// Box up all the details of whether or not we can reset or randomize colors,
// because it's a bit complicated. No reason to muddy up the render logic later!
function useRunColorsController() {
  const rows = useRuns() ?? [];
  const sort: Sort = useRunSetSort();
  const {
    modifiedSort,
    hasActiveFilter,
    hasActiveSearch,
    hasActiveGroupKeys,
    hasCustomRunColors,
    randomizeColors,
    resetColors,
  } = useResetColors(sort, rows);

  const hideRandomizeColors = getShouldHideRandomizeColors(
    rows.length,
    modifiedSort,
    hasActiveFilter,
    hasActiveSearch,
    hasActiveGroupKeys
  );
  const hideResetColors = getShouldHideResetColors(hasCustomRunColors);

  return useMemo(
    () => ({
      randomizeColors,
      resetColors,
      hideRandomizeColors,
      hideResetColors,
    }),
    [randomizeColors, resetColors, hideRandomizeColors, hideResetColors]
  );
}

// Exported separately so ColumnHeaderMenu can check visibility before rendering
// the section, avoiding a separator for a hidden group.
export function useShowRunColors(column: WBTableColumn) {
  const {isSweeps} = useSweepsContext();
  const runs = useRuns();
  const sort = useRunSetSort();
  const isDisplayNameColumn = column.accessor === 'run:displayName';

  const skipRunColorsCheck = isSweeps || !isDisplayNameColumn;
  const {
    modifiedSort,
    hasActiveFilter,
    hasActiveSearch,
    hasActiveGroupKeys,
    hasCustomRunColors,
  } = useResetColorsInfo(sort, runs ?? [], skipRunColorsCheck);
  const hideRandomizeColors = getShouldHideRandomizeColors(
    runs ? runs.length : 0,
    modifiedSort,
    hasActiveFilter,
    hasActiveSearch,
    hasActiveGroupKeys
  );
  return (
    !isSweeps &&
    isDisplayNameColumn &&
    !(hideRandomizeColors && getShouldHideResetColors(hasCustomRunColors))
  );
}

export function RunColorsGroup() {
  const {randomizeColors, resetColors, hideRandomizeColors, hideResetColors} =
    useRunColorsController();

  return (
    <DropdownMenu.Section>
      <DropdownMenu.Header>Run colors</DropdownMenu.Header>
      {hideRandomizeColors ? null : (
        <MenuItem
          data-dd-action-name="Randomize colors"
          icon="randomize"
          onAction={randomizeColors}>
          Randomize colors
        </MenuItem>
      )}
      {hideResetColors ? null : (
        <MenuItem
          data-dd-action-name="Reset colors"
          icon="reset"
          onAction={resetColors}>
          Reset colors
        </MenuItem>
      )}
    </DropdownMenu.Section>
  );
}
